import { i18n } from '@/core/i18n'
import { apiErrorCodeMessage, localizeApiErrorDetail } from '@/core/locales/apiErrorDetails'

type Translator = (key: string, parameters?: Record<string, string | number>) => string
interface ValidationIssue { field?: unknown; code?: unknown; message?: unknown; sourceMessage?: unknown; parameters?: unknown }
interface ApiProblem {
  code?: string; title?: string; detail?: string; error?: string; message?: string; sourceMessage?: string
  errors?: unknown; validationErrors?: unknown; messageCode?: string; messageArguments?: unknown; field?: string
}
const record = (value: unknown): Record<string, unknown> | null => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
export const selectedErrorLocale = (): 'vi' | 'en' => i18n.global.locale.value === 'en' ? 'en' : 'vi'
const translate: Translator = (key, parameters) => i18n.global.te(key, selectedErrorLocale())
  ? parameters ? i18n.global.t(key, parameters) : i18n.global.t(key) : key
function translated(t: Translator, key: string, parameters?: Record<string, string | number>): string {
  const value = t(key, parameters)
  return value && value !== key ? value : ''
}
function parameters(value: unknown): Record<string, string | number> {
  return Object.fromEntries(Object.entries(record(value) ?? {}).filter((entry): entry is [string, string | number] => typeof entry[1] === 'string' || typeof entry[1] === 'number'))
}
const validatorCodes: Record<string, string> = {
  NotEmptyValidator: 'field_required', NotNullValidator: 'field_required', EmailValidator: 'field_invalid_email',
  MinimumLengthValidator: 'field_min_length', MaximumLengthValidator: 'field_max_length', LengthValidator: 'field_length',
  InclusiveBetweenValidator: 'field_range', ExclusiveBetweenValidator: 'field_exclusive_range',
  GreaterThanValidator: 'field_greater_than', GreaterThanOrEqualValidator: 'field_greater_than_or_equal',
  LessThanValidator: 'field_less_than', LessThanOrEqualValidator: 'field_less_than_or_equal',
  EqualValidator: 'field_equal', NotEqualValidator: 'field_not_equal',
  RegularExpressionValidator: 'field_invalid_format', EnumValidator: 'field_invalid_enum'
}
const localizeDetail = (message: unknown, code?: string) => localizeApiErrorDetail(message, selectedErrorLocale(), code, 0,
  (value, slot, source) => slot === '0' && /^\{0\} (?:is required|exceeds|must|end date)/.test(source) ? businessFieldLabel(value) : value)
function fieldMessage(issue: ValidationIssue, t: Translator): string {
  const message = typeof issue.message === 'string' ? issue.message.trim() : ''
  const specific = (message ? translated(t, `errors.${message}`) : '') || localizeDetail(issue.sourceMessage) || localizeDetail(message)
  if (specific) return specific
  if (typeof issue.code === 'string') {
    const code = validatorCodes[issue.code] ?? issue.code
    const localized = translated(t, `errors.${code}`, parameters(issue.parameters))
    if (localized) return localized
  }
  if (/^(?:Required|.*(?:is required\.|must not be empty\.|must not be null\.|field is required\.))$/i.test(message)) return translated(t, 'errors.field_required')
  if (/email address/i.test(message)) return translated(t, 'errors.field_invalid_email')
  return translated(t, 'errors.field_invalid') || translated(t, 'errors.validation_failed')
}
export function businessFieldErrors(error: unknown, t: Translator = translate): Record<string, string[]> {
  const candidate = record(error)
  const response = record(candidate?.response)
  const data = record(response?.data) ?? candidate
  if (!data) return {}
  const result: Record<string, string[]> = Object.create(null)
  const add = (field: string, message: string) => {
    const path = field.charAt(0).toLowerCase() + field.slice(1)
    const list = result[path] ??= []
    if (message && !list.includes(message)) list.push(message)
  }
  if (Array.isArray(data.validationErrors)) {
    for (const value of data.validationErrors) {
      const issue = record(value) as ValidationIssue | null
      if (issue && typeof issue.field === 'string') add(issue.field, fieldMessage(issue, t))
    }
  }
  for (const [field, value] of Object.entries(record(data.errors) ?? {})) {
    if (result[field.charAt(0).toLowerCase() + field.slice(1)]?.length) continue
    for (const message of Array.isArray(value) ? value : [value]) {
      if (typeof message === 'string' && message.trim()) add(field, fieldMessage({ message }, t))
    }
  }
  return result
}
export function businessErrorMessage(t: Translator, data: ApiProblem | undefined | null): string {
  if (!data) return ''
  const fields = businessFieldErrors(data, t)
  if (Object.keys(fields).length) return Object.entries(fields).flatMap(([field, messages]) => messages.map(message => `${businessFieldLabel(field)}: ${message}`)).join('\n')
  if (data.messageCode) {
    const message = translated(t, `errors.${data.messageCode}`, parameters(data.messageArguments))
    if (message) return typeof data.field === 'string' && data.field ? `${businessFieldLabel(data.field)}: ${message}` : message
  }
  const code = data.code || data.error
  for (const message of [data.sourceMessage, data.detail, data.title, data.message]) {
    const localized = localizeDetail(message, code)
    if (localized) return localized
  }
  if (code) return translated(t, `errors.${code}`) || apiErrorCodeMessage(code, selectedErrorLocale())
  return ''
}

const fieldLabels = new Map<string, Map<string, string>>()
const neutralFields: Record<string, readonly [string, string]> = {
  name: ['Tên', 'Name'], code: ['Mã', 'Code'], id: ['Mã định danh', 'Identifier'],
  purpose: ['Mục đích', 'Purpose'], amount: ['Số tiền', 'Amount'], lines: ['Dòng', 'Line'],
  email: ['Email', 'Email'], password: ['Mật khẩu', 'Password'], status: ['Trạng thái', 'Status'],
  startdate: ['Ngày bắt đầu', 'Start date'], enddate: ['Ngày kết thúc', 'End date'],
  fromdate: ['Từ ngày', 'From date'], todate: ['Đến ngày', 'To date'],
  plannedstart: ['Ngày bắt đầu dự kiến', 'Planned start date'], plannedend: ['Ngày kết thúc dự kiến', 'Planned end date'],
  quantity: ['Số lượng', 'Quantity'], version: ['Phiên bản', 'Version'], rowversion: ['Phiên bản dữ liệu', 'Data version'],
  marginpercent: ['Tỷ lệ biên lợi nhuận', 'Margin percent'], profitpercent: ['Tỷ lệ lợi nhuận', 'Profit percent'],
  taxpercent: ['Thuế suất', 'Tax percent'], discountpercent: ['Tỷ lệ chiết khấu', 'Discount percent'],
  files: ['Tệp', 'File'], reason: ['Lý do', 'Reason'], year: ['Năm', 'Year'], month: ['Tháng', 'Month']
}
export function businessFieldLabel(path: string): string {
  const locale = selectedErrorLocale()
  const normalizedPath = path.replace(/[\u0000-\u001f\u007f]/g, ' ').trim()
  if (!normalizedPath) return locale === 'vi' ? 'Thông tin gửi lên' : 'Submitted data'
  let labels = fieldLabels.get(locale)
  if (!labels) {
    labels = new Map()
    const candidates = new Map<string, Set<string>>()
    const visit = (node: unknown) => {
      for (const [key, value] of Object.entries(record(node) ?? {})) {
        if (typeof value === 'string' && value.length <= 60 && !/[{}\n.!?:]/.test(value)) {
          const name = key.replace(/[^a-z0-9]/gi, '').toLowerCase()
          const values = candidates.get(name) ?? new Set<string>()
          values.add(value)
          candidates.set(name, values)
        } else if (record(value)) visit(value)
      }
    }
    visit(i18n.global.getLocaleMessage(locale))
    for (const [key, values] of candidates) if (values.size === 1) labels.set(key, [...values][0])
    for (const [key, values] of Object.entries(neutralFields)) labels.set(key, values[locale === 'vi' ? 0 : 1])
    fieldLabels.set(locale, labels)
  }
  return normalizedPath.split('.').map(segment => {
    const match = /^(.*?)(?:\[(\d+)\])?$/.exec(segment)
    if (!match) return segment
    const name = match[1].replace(/[^a-z0-9]/gi, '').toLowerCase()
    const label = labels!.get(name) ?? match[1]
    return match[2] === undefined ? label : `${label} ${Number(match[2]) + 1}`
  }).join(' • ')
}
const statusCodes: Record<number, string> = {
  400: 'invalid_request', 401: 'unauthorized', 403: 'forbidden', 404: 'not_found', 405: 'method_not_allowed',
  408: 'request_timeout', 409: 'concurrency_conflict', 413: 'file_too_large', 415: 'unsupported_media_type',
  422: 'validation_failed', 429: 'too_many_requests', 500: 'internal_error', 502: 'bad_gateway',
  503: 'service_unavailable', 504: 'gateway_timeout'
}
function errorText(error: unknown, fallbackMessage?: string): string {
  if (!error) return fallbackMessage ?? ''
  const candidate = record(error)
  const response = record(candidate?.response)
  const data = record(response?.data) ?? (response ? null : candidate)
  const message = businessErrorMessage(translate, data as ApiProblem | null)
  if (message) return message
  const transportCode = candidate?.code
  if (transportCode === 'ECONNABORTED' || transportCode === 'ETIMEDOUT') return translate('errors.request_timeout')
  if (transportCode === 'ERR_CANCELED') return translate('errors.request_cancelled')
  if (transportCode === 'session_changed' || candidate?.name === 'SessionChangedError') return translate('errors.session_changed')
  if (!response && (candidate?.isAxiosError || transportCode === 'ERR_NETWORK' || candidate?.message === 'Network Error' || candidate?.message === 'Failed to fetch')) return translate('errors.network_error')
  const detail = localizeDetail(typeof response?.data === 'string' ? response.data : typeof error === 'string' ? error : candidate?.message)
  if (detail) return detail
  const status = typeof response?.status === 'number' ? response.status : undefined
  if (status && status >= 500) return translate(`errors.${statusCodes[status] ?? 'internal_error'}`)
  if (status && [401, 403, 404, 409, 429].includes(status)) return translate(`errors.${statusCodes[status]}`)
  if (fallbackMessage && ![translate('common.error'), translate('errors.unknown')].includes(fallbackMessage)) return fallbackMessage
  if (status && statusCodes[status]) return translate(`errors.${statusCodes[status]}`)
  return fallbackMessage ?? translate('errors.unknown')
}

/** A diagnostic reference only; never copy the error payload or request headers. */
export function businessErrorReference(error: unknown): string | undefined {
  const candidate = record(error)
  const response = record(candidate?.response)
  const data = record(response?.data) ?? candidate
  const headers = record(response?.headers)
  const requestHeaders = record(record(candidate?.config)?.headers)
  const value = data?.traceId ?? headers?.['x-correlation-id'] ?? headers?.['X-Correlation-ID']
    ?? requestHeaders?.['X-Correlation-ID'] ?? requestHeaders?.['x-correlation-id']
  return typeof value === 'string' && /^[a-zA-Z0-9:_-]{1,128}$/.test(value) ? value : undefined
}

export async function copyBusinessErrorReference(error: unknown): Promise<boolean> {
  const reference = businessErrorReference(error)
  if (!reference || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return false
  try {
    await navigator.clipboard.writeText(reference)
    return true
  } catch { return false }
}

export function formatBusinessError(error: unknown, fallbackMessage?: string): string {
  const message = errorText(error, fallbackMessage)
  const candidate = record(error)
  const response = record(candidate?.response)
  const data = record(response?.data) ?? candidate
  const status = response?.status ?? data?.status
  const trace = businessErrorReference(error)
  if (message && (typeof status === 'number' && status >= 500 || ['internal_error', 'database_write_failed'].includes(String(data?.code)))
    && typeof trace === 'string' && /^[a-zA-Z0-9:_-]{1,128}$/.test(trace)) {
    return `${message} (${translate('errors.reference_id')}: ${trace})`
  }
  return message
}
